package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.event.ChangeEvent
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmStatic

/**
 * 复选框。
 *
 * 点击时自动翻转 checked 并触发 onChange；外部通过 [onClick] 绑定的 handler 会在翻转之后被调用，
 * 便于在 DSL 中通过 onClick 派发 action。
 */
class Checkbox private constructor() : UIElement<Checkbox>() {

    private var checkedFlag: Boolean = false
    private var onChangeHandler: UiEventHandler<ChangeEvent<Boolean>>? = null
    private var boundCheckedSignal: Signal<in Boolean>? = null

    // === 视觉覆盖字段（可空，null = 回退主题 token） ===
    private var checkColorValue: Int? = null
    private var focusBorderColorValue: Int? = null
    private var boxBackgroundColorValue: Int? = null
    private var boxBorderColorValue: Int? = null
    private var checkInsetValue: Float? = null
    private var disabledOpacityValue: Float? = null
    private var hoverOverlayColorValue: Int? = null
    private var hoverOverlayOpacityValue: Float? = null
    private var pressedOverlayColorValue: Int? = null
    private var pressedOverlayOpacityValue: Float? = null
    private var focusOverlayColorValue: Int? = null
    private var focusOverlayOpacityValue: Float? = null

    init {
        focusableField = true
        // 默认 onClick：直接翻转 checked。外部通过 onClick(handler) 注册的逻辑会在翻转之后被调用
        super.onClick(UiEventHandler { toggle() })
    }

    override fun onClick(handler: UiEventHandler<MouseEvent>?): Checkbox {
        // 包装：先翻转 checked，再调用外部 handler，保证 DSL 里写 onClick = action() 也能切换状态
        super.onClick(
            UiEventHandler { e ->
                toggle()
                handler?.accept(e)
            },
        )
        return this
    }

    /** 翻转 checked 状态并触发 onChange。 */
    fun toggle(): Checkbox = checked(!checkedFlag)

    protected override fun self(): Checkbox = this

    fun checked(v: Boolean): Checkbox {
        val old = checkedFlag
        checkedFlag = v
        if (old != v) {
            boundCheckedSignal?.set(v)
            onChangeHandler?.accept(ChangeEvent(old, v))
        }
        return this
    }

    fun checked(): Boolean = checkedFlag

    fun onChange(handler: UiEventHandler<ChangeEvent<Boolean>>?): Checkbox {
        onChangeHandler = handler
        return this
    }

    /** 双向绑定到响应式 Signal。 */
    fun bindValue(signal: Signal<in Boolean>): Checkbox {
        // 逆变投影 Signal<in Boolean>：接收任意可写入 Boolean 的信号（Signal<Boolean>/Signal<Object>…），
        // 写入位放开可直接 set(Boolean)，读取位投影为 Any? 交由 toBooleanValue 解码——无需强转。
        boundCheckedSignal = signal
        lifecycleScopeField.register(Effect { checkedFlag = toBooleanValue(signal.get()) })
        return this
    }

    // === 视觉覆盖 fluent setter / getter ===

    fun checkColor(v: Int?): Checkbox {
        checkColorValue = v
        return this
    }

    fun checkColor(): Int? = checkColorValue

    fun focusBorderColor(v: Int?): Checkbox {
        focusBorderColorValue = v
        return this
    }

    fun focusBorderColor(): Int? = focusBorderColorValue

    fun boxBackgroundColor(v: Int?): Checkbox {
        boxBackgroundColorValue = v
        return this
    }

    fun boxBackgroundColor(): Int? = boxBackgroundColorValue

    fun boxBorderColor(v: Int?): Checkbox {
        boxBorderColorValue = v
        return this
    }

    fun boxBorderColor(): Int? = boxBorderColorValue

    fun checkInset(v: Float?): Checkbox {
        checkInsetValue = v
        return this
    }

    fun checkInset(): Float? = checkInsetValue

    fun disabledOpacity(v: Float?): Checkbox {
        disabledOpacityValue = v
        return this
    }

    fun disabledOpacity(): Float? = disabledOpacityValue

    fun hoverOverlayColor(v: Int?): Checkbox {
        hoverOverlayColorValue = v
        return this
    }

    fun hoverOverlayColor(): Int? = hoverOverlayColorValue

    fun hoverOverlayOpacity(v: Float?): Checkbox {
        hoverOverlayOpacityValue = v
        return this
    }

    fun hoverOverlayOpacity(): Float? = hoverOverlayOpacityValue

    fun pressedOverlayColor(v: Int?): Checkbox {
        pressedOverlayColorValue = v
        return this
    }

    fun pressedOverlayColor(): Int? = pressedOverlayColorValue

    fun pressedOverlayOpacity(v: Float?): Checkbox {
        pressedOverlayOpacityValue = v
        return this
    }

    fun pressedOverlayOpacity(): Float? = pressedOverlayOpacityValue

    fun focusOverlayColor(v: Int?): Checkbox {
        focusOverlayColorValue = v
        return this
    }

    fun focusOverlayColor(): Int? = focusOverlayColorValue

    fun focusOverlayOpacity(v: Float?): Checkbox {
        focusOverlayOpacityValue = v
        return this
    }

    fun focusOverlayOpacity(): Float? = focusOverlayOpacityValue

    private fun toBooleanValue(v: Any?): Boolean {
        // 加固：若误把 onChange 的事件 Map（{value=Boolean,...}）写回 checked state，取 value 字段兜底
        val mapValue = (v as? Map<*, *>)?.get("value")
        return when {
            v is Boolean -> v
            mapValue is Boolean -> mapValue
            else -> throw IllegalStateException("Checkbox 绑定的 checked state 必须是布尔值: $v")
        }
    }

    companion object {
        /** 创建一个复选框。 */
        @JvmStatic
        fun create(): Checkbox = Checkbox()
    }
}
